import { RefObject, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useTranslation } from 'react-i18next';

import {
  CloseButton,
  ModalTitle,
  PrimaryButton,
  CancelButton,
  ScriptsDialog,
  ScriptsModalContent,
  ScriptsModalHeader,
  HeaderTitleRow,
  HeaderActionsRow,
  UnsavedChangesRow,
  UnsavedChangesDot,
  UnsavedChangesText,
  ScriptsModalBody,
  ScriptsModalActions,
} from '../AssistantScriptDialog.styled';
import {
  SummaryTitleBlock,
  SummaryDescription,
  SummaryList,
  LanguageBadgeRow,
  LanguageBadge,
} from './styled';
import SummaryRow from '../SummaryRow';
import QuestionShell from '../QuestionShell';
import QuestionBody from '../QuestionBody';
import { QUESTION_META, getVisibleQuestions, QuestionKey } from '../questions';
import { PlaybackPolicy } from '@shared/interfaces/assistantScript';
import { POLICIES } from '../PlaybackPolicySection/constants';
import {
  TriggerAreaCard,
  TriggerAreaCardLabel,
  TriggerAreaCardTitle,
  TriggerAreaCardPinIcon,
  TriggerAreaCardObjectIcon,
  TriggerToggleTouchIcon,
} from '../TriggerSection/styled';
import {
  InterruptMenuPauseIcon,
  InterruptMenuStopIcon,
  InterruptMenuLockIcon,
} from '../InterruptSection/styled';
import { isLanguageComplete } from '../LanguageTabs';
import { languageFlags, languageLabels } from '../constants';
import type { ScriptDialogState } from '../types';

const INTERRUPT_ICONS = {
  finish_step: <InterruptMenuPauseIcon />,
  hard_cut: <InterruptMenuStopIcon />,
  non_interruptible: <InterruptMenuLockIcon />,
};

interface EditSummaryProps {
  open: boolean;
  state: ScriptDialogState;
  paperRef?: RefObject<HTMLDivElement | undefined>;
  onClose: () => void;
}

const EditSummary = ({ open, state, paperRef, onClose }: EditSummaryProps) => {
  const { t } = useTranslation('common');
  const [editingSection, setEditingSection] = useState<QuestionKey | null>(
    null,
  );
  // Opening the languages question shouldn't land on a preselected tab —
  // reset each time it's (re)opened, flip once the user actually clicks one.
  const [languagesTabPicked, setLanguagesTabPicked] = useState(false);

  // The steps list isn't in the DOM yet on the click that first picks a
  // language (it's swapped in for the placeholder on the next render), so
  // LanguageTabs's own synchronous scrollIntoView has nothing to target
  // yet. Once the pick is committed and the steps list has mounted, scroll
  // to it here instead.
  useEffect(() => {
    if (editingSection === 'languages' && languagesTabPicked) {
      state.stepsHeaderRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [editingSection, languagesTabPicked, state.stepsHeaderRef]);

  // Switching into a question view reuses the same scrollable modal body, so
  // without this it can open mid-scroll if the summary list was scrolled down.
  useEffect(() => {
    if (editingSection) {
      state.modalBodyRef.current?.scrollTo({ top: 0 });
    }
  }, [editingSection, state.modalBodyRef]);

  const visibleQuestions = getVisibleQuestions(state.triggerType);
  const displayTitle = state.title ?? state.assistantScript?.title;

  const triggerIcon =
    state.triggerType === 'object' ? (
      <TriggerAreaCardObjectIcon />
    ) : state.triggerType === 'ui_click' ? (
      <TriggerToggleTouchIcon />
    ) : (
      <TriggerAreaCardPinIcon />
    );
  const triggerAreaLabel =
    state.triggerType === 'object'
      ? t('assistant.scriptDialog.triggerObject')
      : state.triggerType === 'custom_spot'
      ? t('assistant.scriptDialog.triggerCustomSpot')
      : t('assistant.scriptDialog.triggerArea');

  const dialogChrome = (children: React.ReactNode) => (
    <ScriptsDialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return;
        }

        onClose();
      }}
      fullWidth
      $theme={state.modalTheme}
      PaperProps={{ ref: paperRef }}
    >
      <ScriptsModalContent>{children}</ScriptsModalContent>
    </ScriptsDialog>
  );

  if (editingSection) {
    const meta = QUESTION_META[editingSection];

    return dialogChrome(
      <>
        <ScriptsModalHeader>
          <HeaderTitleRow>
            <CancelButton
              onClick={() => setEditingSection(null)}
              sx={{
                height: 36,
                minWidth: 'unset',
                padding: '6px 14px',
                paddingLeft: 0,
                border: 0,
                '&:hover': {
                  border: 0,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16, mr: 0.75 }} />
              {t('assistant.scriptDialog.wizard.back')}
            </CancelButton>
          </HeaderTitleRow>
          <HeaderActionsRow>
            {state.hasChanges && (
              <UnsavedChangesRow>
                <UnsavedChangesDot />
                <UnsavedChangesText>
                  {t('assistant.scriptDialog.unsavedChanges')}
                </UnsavedChangesText>
              </UnsavedChangesRow>
            )}
          </HeaderActionsRow>
        </ScriptsModalHeader>

        <ScriptsModalBody ref={state.modalBodyRef}>
          <QuestionShell
            eyebrowKey={meta.eyebrowKey}
            titleKey={meta.titleKey}
            helperKey={meta.helperKey}
          >
            <QuestionBody
              which={editingSection}
              state={state}
              languagesForceNoSelection={
                editingSection === 'languages' && !languagesTabPicked
              }
              onLanguagesTabPick={() => setLanguagesTabPicked(true)}
            />
          </QuestionShell>
        </ScriptsModalBody>

        <ScriptsModalActions>
          <CancelButton onClick={() => setEditingSection(null)}>
            {t('assistant.scriptDialog.cancel')}
          </CancelButton>
          <PrimaryButton onClick={() => setEditingSection(null)}>
            {t('assistant.scriptDialog.wizard.done')}
          </PrimaryButton>
        </ScriptsModalActions>
      </>,
    );
  }

  const firstStepText = state.scriptSteps[0]?.text;
  const translatedCount = state.nonBaseLangs.filter((lang) =>
    isLanguageComplete(state.scriptSteps, lang),
  ).length;

  const renderRow = (key: QuestionKey) => {
    if (key === 'trigger') {
      const value =
        state.triggerType === 'ui_click'
          ? t('assistant.scriptDialog.triggerUiClick')
          : state.triggerType === 'object'
          ? t('assistant.scriptDialog.triggerObject')
          : state.triggerType === 'custom_spot'
          ? t('assistant.scriptDialog.triggerCustomSpot')
          : t('assistant.scriptDialog.triggerAreaEntry');
      const detail =
        state.triggerType === 'ui_click'
          ? `${t('assistant.scriptDialog.buttonLabel')}: "${
              state.uiClickButtonLabel
            }"`
          : displayTitle;

      return (
        <SummaryRow
          key={key}
          icon={triggerIcon}
          label={t('assistant.scriptDialog.trigger')}
          value={value}
          detail={detail}
          onEdit={() => setEditingSection('trigger')}
        />
      );
    }

    if (key === 'policy') {
      const policy = POLICIES.find((p) => p.id === state.playbackPolicy);

      return (
        <SummaryRow
          key={key}
          icon={policy?.icon}
          label={t('assistant.scriptDialog.playbackPolicy')}
          value={t(`assistant.scriptDialog.policies.${state.playbackPolicy}.name`)}
          detail={
            state.triggerType === 'ui_click' &&
            state.playbackPolicy === PlaybackPolicy.ALWAYS
              ? t('assistant.scriptDialog.wizard.policyAlwaysUiClickDesc')
              : t(`assistant.scriptDialog.policies.${state.playbackPolicy}.desc`)
          }
          onEdit={
            state.triggerType === 'ui_click'
              ? undefined
              : () => setEditingSection('policy')
          }
        />
      );
    }

    if (key === 'interrupt') {
      return (
        <SummaryRow
          key={key}
          icon={INTERRUPT_ICONS[state.interruptMode]}
          label={t('assistant.scriptDialog.whenInterrupted')}
          value={t(`assistant.scriptDialog.interrupt${
            state.interruptMode === 'finish_step'
              ? 'FinishStep'
              : state.interruptMode === 'hard_cut'
              ? 'HardCut'
              : 'NonInterruptible'
          }`)}
          detail={
            state.allowSkipDialog
              ? t('assistant.scriptDialog.allowSkipDialog')
              : undefined
          }
          onEdit={() => setEditingSection('interrupt')}
        />
      );
    }

    if (key === 'dialogue') {
      return (
        <SummaryRow
          key={key}
          icon={
            <ArticleOutlinedIcon
              sx={{ fontSize: 18, color: 'var(--am-text-secondary)' }}
            />
          }
          label={t('assistant.scriptDialog.scriptSteps')}
          value={t(
            state.scriptSteps.length === 1
              ? 'assistant.scriptDialog.wizard.stepsCountSingular'
              : 'assistant.scriptDialog.wizard.stepsCountPlural',
            { count: state.scriptSteps.length },
          )}
          detail={
            firstStepText
              ? `"${firstStepText.slice(0, 60)}${
                  firstStepText.length > 60 ? '…' : ''
                }"`
              : t('assistant.scriptDialog.wizard.noDialogueYet')
          }
          onEdit={() => setEditingSection('dialogue')}
        />
      );
    }

    // languages
    return (
      <SummaryRow
        key={key}
        icon="🌐"
        label={t('assistant.scriptDialog.language')}
        value={t('assistant.scriptDialog.wizard.languagesProgress', {
          translated: translatedCount,
          total: state.nonBaseLangs.length,
        })}
        onEdit={() => {
          setLanguagesTabPicked(false);
          setEditingSection('languages');
        }}
      >
        <LanguageBadgeRow>
          {state.nonBaseLangs.map((lang) => {
            const complete = isLanguageComplete(state.scriptSteps, lang);

            return (
              <LanguageBadge key={lang} $complete={complete} title={languageLabels[lang]}>
                <span>{languageFlags[lang]}</span>
                {complete ? '✓' : '—'}
              </LanguageBadge>
            );
          })}
        </LanguageBadgeRow>
      </SummaryRow>
    );
  };

  return dialogChrome(
    <>
      <ScriptsModalHeader>
        <HeaderTitleRow>
          <ArticleOutlinedIcon
            sx={{ color: 'var(--am-icon-accent)', fontSize: 22 }}
          />
          <ModalTitle sx={{ fontWeight: 400 }}>
            {t('assistant.scriptDialog.updateScript')}
          </ModalTitle>
        </HeaderTitleRow>
        <HeaderActionsRow>
          {state.hasChanges && (
            <UnsavedChangesRow>
              <UnsavedChangesDot />
              <UnsavedChangesText>
                {t('assistant.scriptDialog.unsavedChanges')}
              </UnsavedChangesText>
            </UnsavedChangesRow>
          )}
          <CloseButton onClick={onClose} size="small">
            <CloseIcon />
          </CloseButton>
        </HeaderActionsRow>
      </ScriptsModalHeader>

      <ScriptsModalBody ref={state.modalBodyRef}>
        <SummaryTitleBlock>
          <TriggerAreaCard
            sx={{
              width: 'fit-content',
              marginBottom: '26px',
              padding: '12px 14px',
            }}
          >
            <TriggerAreaCardPinIcon />
            <Box>
              <TriggerAreaCardLabel>{triggerAreaLabel}</TriggerAreaCardLabel>
              <TriggerAreaCardTitle>{displayTitle}</TriggerAreaCardTitle>
            </Box>
          </TriggerAreaCard>
          <SummaryDescription>
            <Trans
              i18nKey="assistant.scriptDialog.wizard.summaryDescription"
              components={{ bold: <b /> }}
            />
          </SummaryDescription>
        </SummaryTitleBlock>
        <SummaryList>{visibleQuestions.map(renderRow)}</SummaryList>
      </ScriptsModalBody>

      <ScriptsModalActions>
        <CancelButton onClick={onClose}>
          {t('assistant.scriptDialog.cancel')}
        </CancelButton>
        <PrimaryButton
          onClick={() => state.formik.handleSubmit()}
          disabled={!state.hasChanges || state.isSubmitting}
        >
          {t('assistant.scriptDialog.saveChanges')}
        </PrimaryButton>
      </ScriptsModalActions>
    </>,
  );
};

export default EditSummary;
